/**
 * Hand-maintained runtime for the generated route files emitted by
 * protoc-gen-family-http. Owns the {success, data, meta} envelope shape
 * and the path-template substitution shared by every generated client.
 *
 * Lives outside web/src/infra/contracts/ because that directory is wiped
 * by `buf generate` (clean: true). The generator imports
 * `../contracts-runtime` from each *.routes.ts.
 */

export interface EnvelopeError {
  code: string;
  message: string;
}

export interface EnvelopeMeta {
  timestamp: string;
  request_id?: string;
}

export interface Envelope<T> {
  success: boolean;
  data: T;
  meta: EnvelopeMeta;
  error?: EnvelopeError;
}

/**
 * Substitute `{name}` placeholders in template with values from params.
 * Each value is URI-encoded. Missing values throw — generated callers always
 * pass every path param.
 */
export function buildPath(template: string, params: Record<string, string>): string {
  return template.replace(/\{([^}=]+)(?:=[^}]+)?\}/g, (_match, key: string) => {
    const v = params[key];
    if (v === undefined || v === null || v === '') {
      throw new Error(`buildPath: missing path parameter "${key}" for template "${template}"`);
    }
    return encodeURIComponent(v);
  });
}

/**
 * Validate the server envelope and return its data payload. Throws on
 * `success=false` — callers receive the same surface as a network error,
 * so existing axios interceptors keep working.
 */
export function unwrapEnvelope<T>(env: unknown): T {
  if (!env || typeof env !== 'object') {
    throw new Error('unwrapEnvelope: response body is not an object');
  }
  const e = env as Envelope<T>;
  if (e.success !== true) {
    const code = e.error?.code ?? 'UNKNOWN';
    const message = e.error?.message ?? 'request failed';
    const err = new Error(`${code}: ${message}`) as Error & { code?: string };
    err.code = code;
    throw err;
  }
  return e.data;
}
